import { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";

import {
  applySidebarScroll,
  clearSidebarScrollPosition,
  forgetLegacySidebarScrollPosition,
  readSidebarScrollPosition,
  shouldIgnoreTransientSidebarScroll,
  writeSidebarScrollPosition,
} from "@/lib/sidebar-scroll-position.js";

const RESTORE_HOLD_MS = 320;
const RESTORE_WAIT_MS = 1200;

export function useSidebarScrollPosition({ enabled = true, restoreEpoch } = {}) {
  const savedScrollPositionRef = useRef(readSidebarScrollPosition());
  const contentRef = useRef(null);
  const enabledRef = useRef(enabled);
  const [contentEl, setContentEl] = useState(null);
  const lockCountRef = useRef(0);
  enabledRef.current = enabled;

  const setSidebarContentRef = useCallback((node) => {
    contentRef.current = node;
    setContentEl((previous) => (previous === node ? previous : node));
  }, []);

  const saveScrollPosition = useCallback(() => {
    if (!enabledRef.current) return;
    const element = contentRef.current;
    if (!element || lockCountRef.current > 0) return;
    if (shouldIgnoreTransientSidebarScroll(element, savedScrollPositionRef.current)) {
      return;
    }
    const next = element.scrollTop || 0;
    savedScrollPositionRef.current = next;
    writeSidebarScrollPosition(next);
  }, []);

  const clearScrollPosition = useCallback(() => {
    savedScrollPositionRef.current = 0;
    clearSidebarScrollPosition();
  }, []);

  useEffect(() => {
    forgetLegacySidebarScrollPosition();
  }, []);

  useEffect(() => {
    if (!enabled || !contentEl) return undefined;

    const handleScroll = () => {
      if (lockCountRef.current > 0) {
        applySidebarScroll(contentEl, savedScrollPositionRef.current);
        return;
      }
      saveScrollPosition();
    };

    contentEl.addEventListener("scroll", handleScroll, { passive: true });
    return () => {
      contentEl.removeEventListener("scroll", handleScroll);
    };
  }, [contentEl, enabled, saveScrollPosition]);

  useLayoutEffect(() => {
    if (!enabled || !contentEl) return undefined;

    const target = savedScrollPositionRef.current;
    lockCountRef.current += 1;

    const apply = () => applySidebarScroll(contentEl, target);

    apply();

    const observer = new MutationObserver(apply);
    observer.observe(contentEl, { childList: true, subtree: true });

    let rafId = 0;
    let holdId = 0;
    let finished = false;

    const finishLock = () => {
      if (finished) return;
      finished = true;
      cancelAnimationFrame(rafId);
      clearTimeout(holdId);
      observer.disconnect();
      lockCountRef.current = Math.max(0, lockCountRef.current - 1);
    };

    const startedAt = Date.now();
    const tick = () => {
      if (finished) return;
      const applied = apply();
      if (applied) {
        holdId = setTimeout(finishLock, RESTORE_HOLD_MS);
        return;
      }
      if (Date.now() - startedAt >= RESTORE_WAIT_MS) {
        finishLock();
        return;
      }
      rafId = requestAnimationFrame(tick);
    };

    rafId = requestAnimationFrame(tick);
    const failsafeId = setTimeout(finishLock, RESTORE_WAIT_MS + RESTORE_HOLD_MS + 50);

    return () => {
      clearTimeout(failsafeId);
      finishLock();
    };
  }, [contentEl, enabled, restoreEpoch]);

  useEffect(() => {
    const persist = () => saveScrollPosition();
    window.addEventListener("pagehide", persist);
    return () => {
      persist();
      window.removeEventListener("pagehide", persist);
    };
  }, [saveScrollPosition]);

  return {
    setSidebarContentRef,
    saveScrollPosition,
    clearScrollPosition,
  };
}
